import React from 'react'
import { Button, ConfirmationModal, PageTitle } from 'src/components'
import { useStyles } from './styles'
import { usePregnancyDiagnosisForm } from './hooks/usePregnancyDiagnosisForm'
import { montaTypeOptions } from 'src/constants/montaType'
import ROUTES from 'src/constants/routes'
import { DiagnosisModal } from '../components/DiagnosisModal'
import { LoadingOverlay } from 'src/components/LoadingOverlay'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { PregnancyDiagnosisParams } from 'src/domain/coverage-process'
import { yupResolver } from '@hookform/resolvers/yup'
import { AnimalSelectionSection } from './components/AnimalSelectionSection'
import { ReproductionSection } from './components/ReproductionSection'
import { DiagnosisFormSection } from './components/DiagnosisFormSection'
import { VALIDATIONS } from 'src/@types/validations'

export type PregnancyDiagnosisFormParams = PregnancyDiagnosisParams & { dataFim?: string }

export const PregnancyDiagnosis = () => {
  const formMethods = useForm<PregnancyDiagnosisFormParams>({
    resolver: yupResolver(VALIDATIONS.Diagnosis),
    defaultValues: {
      idAnimal: '',
      dataDiagnostico: new Date().toISOString(),
      tipoReproducao: 'monta',
      touro: '',
      dataCobertura: new Date().toISOString(),
      dataInicial: new Date().toISOString(),
      dataParto: '',
      dataFim: ''
    }
  })

  return (
    <FormProvider {...formMethods}>
      <PregnancyDiagnosisComponent />
    </FormProvider>
  )
}

export const PregnancyDiagnosisComponent: React.FC = () => {
  const style = useStyles()

  const {
    watch,
    reset,
    formState: { errors }
  } = useFormContext<PregnancyDiagnosisFormParams>()

  const {
    selectedAnimal,
    savedBirths,
    isDiagnosisModalOpen,
    useDateInterval,
    isLoadingAnimal,
    handleSelectAnimal,
    handleToggleDateInterval,
    handleCloseDiagnosisModal,
    handleOpenDiagnosisModal,
    handleSaveBirths,
    handleRemoveBirth,
    handleSaveAll,
    showDiagnosisWarningModal,
    handleCancelWarningDiagnosisModal,
    handleConfirmWarningDiagnosisModal
  } = usePregnancyDiagnosisForm()

  const selectedAnimalId = watch('idAnimal')
  const tipoReproducao = watch('tipoReproducao')
  const dataDiagnostico = watch('dataDiagnostico')
  const diagnostico = watch('diagnostico')
  const dataCobertura = watch('dataCobertura')
  const dataInicial = watch('dataInicial')
  const dataParto = watch('dataParto')
  const touro = watch('touro')

  return (
    <>
      <LoadingOverlay isVisible={isLoadingAnimal} />

      <div className={style.pregnancyDiagnosisHeader}>
        <PageTitle
          title='Diagnóstico de Gestação'
          className={style.pregnancyDiagnosisTitle}
          breadcrumbLink={{
            to: ROUTES.REPRODUCTION.ROOT,
            text: 'Reprodução'
          }}
        />
      </div>

      <h1 className={style.title}>Diagnóstico</h1>

      <div className={style.propertiesContainer}>
        <AnimalSelectionSection
          selectedAnimalId={selectedAnimalId}
          error={errors?.idAnimal?.message}
          onSelectAnimal={handleSelectAnimal}
        />

        <form>
          <div
            className={`${style.reproductionDiagnosisLayout} ${selectedAnimalId ? style.reproductionDiagnosisLayoutActive : ''}`}
          >
            <ReproductionSection
              useDateInterval={useDateInterval}
              handleToggleDateInterval={handleToggleDateInterval}
            />

            <DiagnosisFormSection
              savedBirths={savedBirths}
              isLoadingAnimal={isLoadingAnimal}
              onOpenDiagnosisModal={handleOpenDiagnosisModal}
              onRemoveBirth={handleRemoveBirth}
            />
          </div>

          {selectedAnimalId && (
            <div className={style.actionsContainer}>
              <Button
                type='button'
                className={`${style.buttonWrapper} ${style.buttonOutlined}`}
                onClick={() => reset()}
                outlined
              >
                Cancelar
              </Button>
              <Button type='button' className={style.buttonWrapper} onClick={handleSaveAll}>
                Salvar
              </Button>
            </div>
          )}
        </form>
      </div>
      {diagnostico === 'prenha' && (
        <DiagnosisModal
          initView={'animalForm'}
          isOpen={isDiagnosisModalOpen}
          onClose={handleCloseDiagnosisModal}
          motherAnimalId={selectedAnimalId || ''}
          motherAnimalName={selectedAnimal?.nome || ''}
          fatherAnimalId={touro || ''}
          diagnosisDate={dataParto || dataDiagnostico || ''}
          reproductionType={montaTypeOptions.find((opt) => opt.id === tipoReproducao)?.value || 'Monta Natural'}
          reproductionDate={useDateInterval ? dataInicial : dataCobertura || ''}
          onSaveAll={handleSaveBirths}
          existingBirths={savedBirths}
        />
      )}

      <ConfirmationModal
        isOpen={showDiagnosisWarningModal}
        title='Atenção'
        message='Não existe uma gestação em andamento para o animal selecionado. Uma nova gestação será criada.'
        confirmText='Sim, continuar'
        cancelText='Não, cancelar'
        onConfirm={handleConfirmWarningDiagnosisModal}
        onCancel={handleCancelWarningDiagnosisModal}
      />
    </>
  )
}
